import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Clock, Globe, ListChecks, Loader2, Mic, MicOff, Video, VideoOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useClaudeAI } from '../hooks/useClaudeAI';
import { api } from '../utils/api';
import DailyLimitModal from '../components/DailyLimitModal';

const TOTAL_QUESTIONS = 5;     // main questions (Q1 fixed, Q2-Q5 adaptive)
const MAX_TOTAL_QUESTIONS = 10; // hard cap including all follow-ups

const scoreInfo = (score) => {
  if (score <= 2) return { label: 'Low', color: '#EF4444', bg: '#FEF2F2' };
  if (score <= 5) return { label: 'Medium', color: '#F59E0B', bg: '#FFFBEB' };
  if (score <= 8) return { label: 'High', color: '#10B981', bg: '#ECFDF5' };
  return { label: 'Max', color: '#8B5CF6', bg: '#F5F3FF' };
};

const weightageInfo = (w) => {
  if (w === 'high')   return { label: 'High Priority',   color: '#EF4444', bg: '#FEF2F2', factor: 1.5 };
  if (w === 'low')    return { label: 'Low Priority',    color: '#6B7280', bg: '#F3F4F6', factor: 0.75 };
  return                     { label: 'Medium Priority', color: '#F59E0B', bg: '#FFFBEB', factor: 1.0 };
};

const fmtTime = (secs) => {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

// Pick the clearest available English voice (natural/neural first)
const pickVoice = (voices) => {
  const preds = [
    v => /Google US English/.test(v.name),
    v => /Microsoft Aria Online/.test(v.name),
    v => /Microsoft Jenny Online/.test(v.name),
    v => /Microsoft Zira/.test(v.name),
    v => /Samantha/.test(v.name),
    v => v.lang === 'en-US' && /natural|neural|online/i.test(v.name),
    v => v.lang === 'en-US',
    v => v.lang.startsWith('en'),
  ];
  for (const p of preds) { const v = voices.find(p); if (v) return v; }
  return null;
};

const FILLER_WORDS = new Set(['um','uh','like','you know','basically','literally','actually','right','i mean','hmm','er','ah','so','well']);

// ── Candidate "cross-question" handling ─────────────────────
// Instant pattern matches for the common cases; anything else that merely
// *looks* like a question falls back to a quick AI classification instead
// of being scored as an answer.
const MAX_ASIDES_PER_Q = 2; // cap how many times we'll re-ask the same question before forcing progress

const HEARING_RE = /\b(can you hear me|are you able to hear me|is (this|my (mic|audio|voice)) (audible|working)|am i audible|can'?t hear (you|me)|hear me (ok|okay|fine|clearly)|is my mic working|(audio|mic) (issues|problems)|breaking up|cutting out|is my audio (fine|ok|okay|clear))\b/i;
const REPEAT_RE  = /\b(repeat (that|it|the question)|say that again|come again|didn'?t (catch|hear|understand) (that|you)|pardon(\s+me)?|could you repeat|one more time|say it one more time|what was the question)\b/i;
const META_RE     = /\b(what (kind|type) of interview|how many questions|how long (is|will) this (take|be)|what is this interview (for|about)|is this (a )?(mock|real|practice) interview|are you (a real person|human|an ai|a bot|a robot)|who (created|built|made) you|what company is this|what happens (after|next)|will this be (recorded|saved|shared)|is this (being )?recorded)\b/i;

const detectCannedInterjection = (utterance, jobTitle) => {
  if (HEARING_RE.test(utterance)) return { reply: "Yes, I can hear you clearly — your audio is coming through fine. Let's continue." };
  if (REPEAT_RE.test(utterance))  return { reply: 'Sure, let me repeat that question.' };
  if (META_RE.test(utterance))    return { reply: `This is an AI-driven mock video interview for the ${jobTitle} role. I'll ask a few main questions with adaptive follow-ups based on your answers, then give you a detailed score report at the end. Let's get back to it.` };
  return null;
};

const looksLikeQuestion = (utterance) => {
  const s = utterance.trim();
  if (!s || s.split(/\s+/).length > 30) return false;
  if (/\?\s*$/.test(s)) return true;
  return /^(what|why|how|when|where|who|which|can you|could you|do you|does this|is this|are you|will this|would you|sorry|pardon|excuse me)\b/i.test(s);
};

const DEFAULT_BLUEPRINT = (title) => ({
  interviewType: 'mixed',
  difficulty: 'mid',
  plannedTopics: [
    `Core ${title} skills and technical knowledge`,
    'Problem-solving approach and past challenges',
    'Leadership, collaboration, and teamwork',
    'Project delivery and measurable impact',
    'Career goals and motivation for this role',
  ],
});

export const VideoInterviewPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    evaluateInterviewAnswer,
    analyzeInterviewResponse,
    classifyCandidateInterjection,
    generateAdaptiveQuestion,
    generateFinalInterviewReport,
    dailyLimitError, clearDailyLimitError,
  } = useClaudeAI();

  // ── Steps ────────────────────────────────────────────────
  const [step, setStep] = useState('setup');

  // ── Setup ────────────────────────────────────────────────
  const [jobTitle, setJobTitle] = useState('');
  const [jobDescription, setJobDescription] = useState('');

  // ── Ready ────────────────────────────────────────────────
  const [userName, setUserName] = useState(user?.name || '');
  const [userEmail, setUserEmail] = useState(user?.email || '');
  const [cameraGranted, setCameraGranted] = useState(false);
  const [micGranted, setMicGranted] = useState(false);

  // ── Interview ────────────────────────────────────────────
  // phase: intro | waiting-ready | generating-question | ai-speaking | user-answering
  const [phase, setPhase] = useState('intro');
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [currentQIdx, setCurrentQIdx] = useState(0);      // 1-based for display
  const [currentQText, setCurrentQText] = useState('');   // shown in question bubble
  const [sessionTranscript, setSessionTranscript] = useState('');
  const [interimText, setInterimText] = useState('');
  const [transcripts, setTranscripts] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const [showSilencePopup, setShowSilencePopup] = useState(false);
  const [silenceCountdown, setSilenceCountdown] = useState(10);

  // ── Q&A history (grows during interview) ─────────────────
  const [qaHistory, setQaHistory] = useState([]);          // [{question, hint, answer}]

  // ── Report ───────────────────────────────────────────────
  const [evaluations, setEvaluations] = useState([]);

  // ── Adaptive engine ───────────────────────────────────────
  const [blueprint, setBlueprint] = useState(null);
  const [finalReport, setFinalReport] = useState(null);
  const [analysisInProgress, setAnalysisInProgress] = useState(false);
  const [questionDisplay, setQuestionDisplay] = useState('');

  // ── Communication metrics (client-side) ──────────────────
  const [communicationMetrics, setCommunicationMetrics] = useState({ fillerWords: 0, totalWords: 0, answers: 0 });

  // ── Emailed PDF report ───────────────────────────────────
  // idle | sending | sent | failed
  const [emailStatus, setEmailStatus] = useState('idle');
  const [emailError, setEmailError] = useState('');
  const [savedInterviewId, setSavedInterviewId] = useState(null);

  // ── Refs ─────────────────────────────────────────────────
  const videoRef             = useRef(null);
  const streamRef            = useRef(null);
  const recognitionRef       = useRef(null);
  const voicesRef            = useRef([]);
  const sessionTranscriptRef = useRef('');
  const lastSpeechTimeRef    = useRef(Date.now());
  const phaseRef             = useRef('intro');
  const qaHistoryRef         = useRef([]);      // mirrors qaHistory state
  const currentQRef          = useRef(null);    // current {question, hint} object
  const silenceTimerRef      = useRef(null);
  const silencePopupCheckRef = useRef(null);
  const transcriptSidebarRef = useRef(null);
  const advanceFnRef         = useRef(null);
  const stoppedRef           = useRef(false);
  const jobTitleRef          = useRef('');
  const blueprintRef         = useRef(null);
  const mainQCountRef        = useRef(0);
  const followUpsThisQRef    = useRef(0);
  const asidesThisQRef       = useRef(0);   // # of "cross-question" resolutions for the current question
  const communicationMetricsRef = useRef({ fillerWords: 0, totalWords: 0, answers: 0 });
  const elapsedRef           = useRef(0);

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { jobTitleRef.current = jobTitle; }, [jobTitle]);

  const trackCommunication = (text) => {
    const words = text.toLowerCase().split(/\s+/).filter(Boolean);
    const fillerCount = words.filter(w => FILLER_WORDS.has(w)).length;
    const updated = {
      fillerWords: communicationMetricsRef.current.fillerWords + fillerCount,
      totalWords:  communicationMetricsRef.current.totalWords  + words.length,
      answers:     communicationMetricsRef.current.answers     + 1,
    };
    communicationMetricsRef.current = updated;
    setCommunicationMetrics({ ...updated });
  };

  const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  // ── Load voices on mount (async in Chrome) ───────────────
  useEffect(() => {
    const load = () => { voicesRef.current = window.speechSynthesis?.getVoices() || []; };
    load();
    window.speechSynthesis?.addEventListener('voiceschanged', load);
    return () => window.speechSynthesis?.removeEventListener('voiceschanged', load);
  }, []);

  // ── Camera ───────────────────────────────────────────────
  const attachVideo = (stream) => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  };

  useEffect(() => {
    if (streamRef.current && videoRef.current) attachVideo(streamRef.current);
  }, [step]);

  const requestCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      attachVideo(stream);
      setCameraGranted(true);
      setMicGranted(true);
    } catch { setCameraGranted(false); }
  };

  // ── Text-to-speech (Chrome long-text keep-alive workaround) ──
  const speak = (text, onDone) => {
    if (!window.speechSynthesis) { onDone?.(); return; }
    window.speechSynthesis.cancel();

    const doSpeak = (voices) => {
      if (stoppedRef.current) return;
      const voice = pickVoice(voices);
      const utt = new SpeechSynthesisUtterance(text);
      utt.lang   = 'en-US';
      utt.rate   = 0.88;
      utt.pitch  = 1.05;
      utt.volume = 1.0;
      if (voice) utt.voice = voice;

      // Keep-alive: Chrome pauses TTS after ~15 s on long utterances
      let keepAlive = null;
      utt.onstart = () => {
        keepAlive = setInterval(() => {
          window.speechSynthesis.pause();
          window.speechSynthesis.resume();
        }, 10000);
      };
      const cleanup = () => { clearInterval(keepAlive); };
      utt.onend   = () => { cleanup(); setAiSpeaking(false); if (!stoppedRef.current) onDone?.(); };
      utt.onerror = () => { cleanup(); setAiSpeaking(false); if (!stoppedRef.current) onDone?.(); };

      setAiSpeaking(true);
      window.speechSynthesis.speak(utt);
    };

    if (voicesRef.current.length > 0) {
      doSpeak(voicesRef.current);
    } else {
      // Wait for voices to load (first call in Chrome)
      const handler = () => {
        voicesRef.current = window.speechSynthesis.getVoices();
        window.speechSynthesis.removeEventListener('voiceschanged', handler);
        doSpeak(voicesRef.current);
      };
      window.speechSynthesis.addEventListener('voiceschanged', handler);
      // Fallback in case event never fires
      setTimeout(() => {
        voicesRef.current = window.speechSynthesis.getVoices();
        if (voicesRef.current.length > 0) doSpeak(voicesRef.current);
        else doSpeak([]);
      }, 800);
    }
  };

  // ── Stop recognition + timers ────────────────────────────
  const stopRecognition = () => {
    if (recognitionRef.current) {
      const rec = recognitionRef.current;
      rec.onend = null; rec.onresult = null;
      recognitionRef.current = null;
      try { rec.stop(); } catch (_) {}
    }
    if (silenceTimerRef.current)      { clearTimeout(silenceTimerRef.current);       silenceTimerRef.current = null; }
    if (silencePopupCheckRef.current) { clearInterval(silencePopupCheckRef.current); silencePopupCheckRef.current = null; }
  };

  // ── Shared recognition setup used inside startQuestion ───
  const startListening = (currentQ) => {
    if (!SR) return;
    stopRecognition();
    const rec = new SR();
    rec.continuous       = true;
    rec.interimResults   = true;
    rec.lang             = 'en-US';
    recognitionRef.current = rec;

    rec.onresult = (e) => {
      if (stoppedRef.current) return;
      lastSpeechTimeRef.current = Date.now();
      let interim = '', finalChunk = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalChunk += r[0].transcript + ' ';
        else interim += r[0].transcript;
      }
      if (finalChunk) { sessionTranscriptRef.current += finalChunk; setSessionTranscript(sessionTranscriptRef.current); }
      setInterimText(interim);

      // Reset the "still there?" popup on new speech
      setShowSilencePopup(false);
      setSilenceCountdown(15);
      if (silencePopupCheckRef.current) { clearInterval(silencePopupCheckRef.current); silencePopupCheckRef.current = null; }
      setupPopupChecker();
    };

    rec.onend = () => {
      if (!stoppedRef.current && phaseRef.current === 'user-answering' && recognitionRef.current === rec) {
        try { rec.start(); } catch (_) {}
      }
    };
    try { rec.start(); } catch (_) {}
    setupPopupChecker();
  };

  const setupPopupChecker = () => {
    if (silencePopupCheckRef.current) return;
    silencePopupCheckRef.current = setInterval(() => {
      if (stoppedRef.current || phaseRef.current !== 'user-answering') {
        clearInterval(silencePopupCheckRef.current); silencePopupCheckRef.current = null; return;
      }
      if (Date.now() - lastSpeechTimeRef.current > 15000) {
        clearInterval(silencePopupCheckRef.current); silencePopupCheckRef.current = null;
        setShowSilencePopup(true); setSilenceCountdown(15);
      }
    }, 1000);
  };

  // ── Ask a question: speak it, then listen ────────────────
  const startQuestion = (qIdx, q) => {
    currentQRef.current = q;
    setCurrentQIdx(qIdx + 1); // 1-based for display

    // Compute label for top bar
    const mainsSoFar = qaHistoryRef.current.filter(h => h.type === 'main').length;
    const isFollowUp = q.type === 'clarify' || q.type === 'followup';
    setQuestionDisplay(isFollowUp ? 'Follow-up' : `Q ${mainsSoFar + 1} / ${TOTAL_QUESTIONS}`);

    setPhase('ai-speaking'); phaseRef.current = 'ai-speaking';
    setCurrentQText(q.question);
    setSessionTranscript(''); setInterimText('');
    setShowSilencePopup(false); setSilenceCountdown(15);
    sessionTranscriptRef.current = '';

    setTranscripts(prev => [...prev, { role: 'ai', text: q.question }]);
    speak(q.question, () => {
      if (stoppedRef.current) return;
      setPhase('user-answering'); phaseRef.current = 'user-answering';
      lastSpeechTimeRef.current = Date.now();
      startListening(q);
    });
  };

  // ── Detect a "cross-question" (candidate asked something instead of
  // answering): instant pattern match first, AI fallback for anything else
  // that merely looks like a question. Returns { reply } or null.
  const detectInterjection = async (utterance, questionText, currentJobTitle) => {
    const canned = detectCannedInterjection(utterance, currentJobTitle);
    if (canned) return canned;
    if (!looksLikeQuestion(utterance)) return null;
    try {
      const result = await classifyCandidateInterjection({ jobTitle: currentJobTitle, currentQuestion: questionText, utterance });
      if (result.isQuestion && result.reply) return { reply: result.reply };
    } catch (_) { /* non-fatal: fall through and treat utterance as the answer */ }
    return null;
  };

  // ── Save answer, analyze it, then decide what to ask next ──
  const advanceFromQuestion = async (currentQ, forceEnd = false) => {
    stopRecognition();
    const answer = sessionTranscriptRef.current.trim() || '(No answer provided)';
    sessionTranscriptRef.current = '';
    setSessionTranscript(''); setInterimText('');
    setShowSilencePopup(false);

    // ── Candidate asked something instead of answering ────────
    // (audio check, "repeat that", "what kind of interview is this", etc.)
    // Respond, then re-ask the SAME question — doesn't count as an answer
    // or consume a question slot. Capped so a candidate can't stall forever.
    if (!forceEnd && answer !== '(No answer provided)' && asidesThisQRef.current < MAX_ASIDES_PER_Q) {
      setPhase('generating-question'); phaseRef.current = 'generating-question';
      setCurrentQText('');
      const interjection = await detectInterjection(answer, currentQ.question, jobTitleRef.current);
      if (interjection && !stoppedRef.current) {
        asidesThisQRef.current += 1;
        setTranscripts(prev => [...prev, { role: 'user', text: answer }, { role: 'ai', text: interjection.reply }]);
        speak(interjection.reply, () => {
          if (stoppedRef.current) return;
          startQuestion(qaHistoryRef.current.length, currentQ);
        });
        return;
      }
    }

    // Track filler words / word count from this answer
    if (answer !== '(No answer provided)') trackCommunication(answer);

    const qType = currentQ.type === 'clarify' ? 'clarify'
                : currentQ.type === 'followup' ? 'followup'
                : 'main';

    const qa = { question: currentQ.question, hint: currentQ.hint || '', answer, type: qType, analysis: null };
    const newHistory = [...qaHistoryRef.current, qa];
    qaHistoryRef.current = newHistory;
    setQaHistory(newHistory);
    setTranscripts(prev => [...prev, { role: 'user', text: answer }]);

    if (forceEnd || newHistory.length >= MAX_TOTAL_QUESTIONS) {
      stoppedRef.current = true;
      window.speechSynthesis?.cancel();
      setStep('analyzing');
      return;
    }

    // Show "analyzing answer" indicator in AI panel
    setPhase('generating-question'); phaseRef.current = 'generating-question';
    setCurrentQText('');
    setAnalysisInProgress(true);

    try {
      // Real-time analysis: score + next-action recommendation
      const analysis = await analyzeInterviewResponse({
        question: currentQ.question,
        answer,
        jobTitle: jobTitleRef.current,
        seniority: 'mid',
        interviewHistory: newHistory,
      });

      // Persist analysis on this Q&A entry
      newHistory[newHistory.length - 1] = { ...newHistory[newHistory.length - 1], analysis };
      qaHistoryRef.current = [...newHistory];
      setQaHistory([...newHistory]);

      const MAX_FOLLOWUPS = 2;
      const rec = analysis.recommendation;
      const canFollowUp = followUpsThisQRef.current < MAX_FOLLOWUPS;

      let nextQType;
      if ((rec === 'clarify' || rec === 'followup') && canFollowUp) {
        followUpsThisQRef.current += 1;
        nextQType = rec;
      } else {
        // Advance to next main question
        followUpsThisQRef.current = 0;
        mainQCountRef.current += 1;

        if (mainQCountRef.current >= TOTAL_QUESTIONS) {
          stoppedRef.current = true;
          setAnalysisInProgress(false);
          setStep('analyzing');
          return;
        }
        nextQType = 'next';
      }

      asidesThisQRef.current = 0; // fresh cross-question budget for the new question

      const result = await generateAdaptiveQuestion({
        jobTitle: jobTitleRef.current,
        seniority: 'mid',
        interviewHistory: qaHistoryRef.current,
        blueprint: blueprintRef.current,
        questionType: nextQType,
      });

      setAnalysisInProgress(false);

      if (!stoppedRef.current) {
        const nextIdx = qaHistoryRef.current.length;
        startQuestion(nextIdx, {
          question: result?.question || (
            nextQType === 'clarify'  ? 'Can you give me a specific example of what you just described?' :
            nextQType === 'followup' ? 'Can you elaborate on that a bit more?' :
            `What is the most significant challenge you've faced as a ${jobTitleRef.current}?`
          ),
          hint: result?.hint || '',
          type: nextQType === 'next' ? 'main' : nextQType,
        });
      }

    } catch {
      setAnalysisInProgress(false);
      followUpsThisQRef.current = 0;
      asidesThisQRef.current = 0;
      mainQCountRef.current += 1;

      if (mainQCountRef.current >= TOTAL_QUESTIONS || stoppedRef.current) {
        stoppedRef.current = true;
        setStep('analyzing');
        return;
      }

      if (!stoppedRef.current) {
        const nextIdx = qaHistoryRef.current.length;
        startQuestion(nextIdx, {
          question: `Can you describe a challenge you've overcome in a ${jobTitleRef.current} role and what you learned?`,
          hint: 'Tests problem-solving and self-awareness',
          type: 'main',
        });
      }
    }
  };

  // Keep advanceFnRef always pointing to the latest version
  advanceFnRef.current = advanceFromQuestion;

  // ── Intro: greeting + "are you ready?" ───────────────────
  const playIntro = (name) => {
    const firstName = (name || '').split(' ')[0] || 'there';
    const msg = `Hey ${firstName}, this is MarsResume AI and I will be conducting your interview today. Are you ready?`;
    setPhase('intro'); phaseRef.current = 'intro';
    setCurrentQText(msg);
    setTranscripts([{ role: 'ai', text: msg }]);
    speak(msg, () => waitForReady(firstName));
  };

  // ── Wait for "yes / ready" response ──────────────────────
  const waitForReady = (firstName) => {
    if (stoppedRef.current) return;
    setPhase('waiting-ready'); phaseRef.current = 'waiting-ready';
    lastSpeechTimeRef.current = Date.now();

    const proceedToQ1 = () => {
      stopRecognition();
      const q1 = {
        question: `Great! Let's begin. Please tell me about yourself and walk me through your professional experience relevant to the ${jobTitleRef.current} position.`,
        hint: 'Assesses communication, background and role relevance',
        type: 'main',
      };
      startQuestion(0, q1);
    };

    if (!SR) { setTimeout(proceedToQ1, 3000); return; }

    stopRecognition();
    const rec = new SR();
    rec.lang = 'en-US'; rec.continuous = false; rec.interimResults = true;
    recognitionRef.current = rec;

    rec.onresult = (e) => {
      if (stoppedRef.current) return;
      lastSpeechTimeRef.current = Date.now();
      let text = '';
      for (let i = e.resultIndex; i < e.results.length; i++) text += e.results[i][0].transcript.toLowerCase();

      setTranscripts(prev => [...prev, { role: 'user', text: text || 'Ready!' }]);

      // Proceed after a brief pause following any speech
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(proceedToQ1, 1500);

      // Immediate on obvious ready-keywords
      if (/\b(yes|yeah|yep|ready|sure|ok|okay|go|start|begin|let|proceed)\b/.test(text)) {
        clearTimeout(silenceTimerRef.current);
        proceedToQ1();
      }
    };

    rec.onend = () => {
      if (phaseRef.current === 'waiting-ready' && !stoppedRef.current && recognitionRef.current === rec) {
        try { rec.start(); } catch (_) {}
      }
    };

    try { rec.start(); } catch (_) {}

    // Auto-proceed after 8 s even if no response
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => {
      if (phaseRef.current === 'waiting-ready' && !stoppedRef.current) proceedToQ1();
    }, 8000);
  };

  // ── Silence popup countdown ───────────────────────────────
  useEffect(() => {
    if (!showSilencePopup) return;
    if (silenceCountdown <= 0) {
      setShowSilencePopup(false);
      advanceFnRef.current(currentQRef.current || { question: '', hint: '' }, true);
      return;
    }
    const t = setTimeout(() => setSilenceCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [showSilencePopup, silenceCountdown]);

  // ── Elapsed timer ─────────────────────────────────────────
  useEffect(() => {
    if (step !== 'interview') return;
    const t = setInterval(() => setElapsed(e => { elapsedRef.current = e + 1; return e + 1; }), 1000);
    return () => clearInterval(t);
  }, [step]);

  // ── Email the PDF report ─────────────────────────────────
  // Fired automatically once the interview is saved; also re-runnable from the
  // report screen if the first attempt failed.
  const sendReportEmail = async (interviewId, email) => {
    if (!interviewId) return;
    setEmailStatus('sending'); setEmailError('');
    try {
      await api.emailInterviewReport(interviewId, email);
      setEmailStatus('sent');
    } catch (e) {
      setEmailStatus('failed');
      setEmailError(e.message || 'Could not send the report email.');
    }
  };

  // ── Analysis ─────────────────────────────────────────────
  useEffect(() => {
    if (step !== 'analyzing') return;
    let cancelled = false;

    const analyze = async () => {
      const qa = qaHistoryRef.current;

      // Detailed per-Q evaluation (for report cards: strengths, improvements, weightage)
      const results = [];
      for (let i = 0; i < qa.length; i++) {
        if (cancelled) break;
        const { question, hint, answer } = qa[i];
        if (!answer || answer === '(No answer provided)') {
          results.push({ score: 0, weightage: 'low', strengths: [], improvements: ['No answer was provided.'], suggestedAnswer: '' });
          continue;
        }
        try {
          const ev = await evaluateInterviewAnswer({ question, hint, userAnswer: answer, jobTitle, seniority: 'mid' });
          results.push(ev);
        } catch {
          results.push({ score: 0, weightage: 'medium', strengths: [], improvements: ['Unable to evaluate.'], suggestedAnswer: '' });
        }
      }
      if (cancelled) return;
      setEvaluations(results);

      const answeredQs = qa.filter(q => q.answer && q.answer !== '(No answer provided)');

      // Comprehensive final report — only when real answers exist
      let report = null;
      if (answeredQs.length >= 1) {
        try {
          report = await generateFinalInterviewReport({
            jobTitle,
            seniority: 'mid',
            interviewHistory: qa,
            communicationMetrics: communicationMetricsRef.current,
          });
          if (!cancelled) setFinalReport(report);
        } catch { /* non-fatal */ }
      }

      // Save to DB — skip empty sessions entirely
      if (answeredQs.length >= 1) {
        try {
          const saved = await api.createInterview({
            title: jobTitle,
            mode: 'video',
            seniority: 'mid',
            jobAd: jobDescription,
            duration: elapsedRef.current,
            videoReport: report,
            communicationMetrics: communicationMetricsRef.current,
            questions: qa.map((q, i) => ({
              question: q.question,
              hint: q.hint,
              type: q.type || 'main',
              practice: {
                userAnswer: q.answer,
                ...(results[i] || {}),
                ...(q.analysis ? { realtimeScore: q.analysis.score, recommendation: q.analysis.recommendation } : {}),
                practicedAt: new Date().toISOString(),
              },
            })),
          });

          // Email the PDF report — non-blocking, the report renders either way
          if (!cancelled && saved?._id) {
            setSavedInterviewId(saved._id);
            sendReportEmail(saved._id, userEmail || user?.email);
          }
        } catch (e) {
          if (!cancelled) { setEmailStatus('failed'); setEmailError(e.message || 'Could not save this interview.'); }
        }
      }

      if (!cancelled) setStep('report');
    };
    analyze();
    return () => { cancelled = true; };
  }, [step]); // eslint-disable-line

  // ── Transcript sidebar auto-scroll ───────────────────────
  useEffect(() => {
    if (transcriptSidebarRef.current) {
      transcriptSidebarRef.current.scrollTop = transcriptSidebarRef.current.scrollHeight;
    }
  }, [transcripts]);

  // ── Cleanup on unmount ────────────────────────────────────
  useEffect(() => {
    return () => {
      stoppedRef.current = true;
      stopRecognition();
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      window.speechSynthesis?.cancel();
    };
  }, []); // eslint-disable-line

  // ── Handlers ─────────────────────────────────────────────
  const handleSetupNext = () => { setStep('ready'); requestCamera(); };

  const handleStartInterview = () => {
    stoppedRef.current = false;
    qaHistoryRef.current = [];
    blueprintRef.current = DEFAULT_BLUEPRINT(jobTitle);
    mainQCountRef.current = 0;
    followUpsThisQRef.current = 0;
    asidesThisQRef.current = 0;
    communicationMetricsRef.current = { fillerWords: 0, totalWords: 0, answers: 0 };
    setQaHistory([]);
    setEvaluations([]);
    setFinalReport(null);
    setBlueprint(DEFAULT_BLUEPRINT(jobTitle));
    setCommunicationMetrics({ fillerWords: 0, totalWords: 0, answers: 0 });
    setAnalysisInProgress(false);
    setQuestionDisplay('');
    setEmailStatus('idle'); setEmailError(''); setSavedInterviewId(null);
    setElapsed(0);
    setCurrentQIdx(0);
    setTranscripts([]);
    setStep('interview');
    setTimeout(() => playIntro(userName), 600);
  };

  const handleStopInterview = () => {
    stoppedRef.current = true;
    window.speechSynthesis?.cancel();
    setAiSpeaking(false);
    if (phaseRef.current === 'user-answering') {
      advanceFnRef.current(currentQRef.current || { question: '', hint: '' }, true);
    } else {
      stopRecognition();
      setStep('analyzing');
    }
  };

  const handleResumeFromPopup = () => {
    setShowSilencePopup(false);
    setSilenceCountdown(15);
    lastSpeechTimeRef.current = Date.now();
    stoppedRef.current = false;
    if (phaseRef.current === 'user-answering' && !recognitionRef.current && currentQRef.current) {
      startListening(currentQRef.current);
    }
  };

  const handleTryAgain = () => {
    stoppedRef.current = false;
    qaHistoryRef.current = [];
    currentQRef.current = null;
    blueprintRef.current = null;
    mainQCountRef.current = 0;
    followUpsThisQRef.current = 0;
    asidesThisQRef.current = 0;
    communicationMetricsRef.current = { fillerWords: 0, totalWords: 0, answers: 0 };
    setStep('setup'); setJobTitle(''); setJobDescription('');
    setEvaluations([]); setQaHistory([]); setTranscripts([]);
    setFinalReport(null); setBlueprint(null); setAnalysisInProgress(false);
    setCommunicationMetrics({ fillerWords: 0, totalWords: 0, answers: 0 });
    setQuestionDisplay('');
    setEmailStatus('idle'); setEmailError(''); setSavedInterviewId(null);
    setElapsed(0); setCurrentQIdx(0); setSessionTranscript(''); setInterimText('');
    setShowSilencePopup(false); setCameraGranted(false); setMicGranted(false);
  };

  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────

  if (step === 'setup') {
    const canProceed = jobTitle.trim() && jobDescription.trim();
    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FB', display: 'flex', flexDirection: 'column', fontFamily: 'Inter,sans-serif' }}>
        <div style={{ background: 'white', borderBottom: '1px solid #EAECF0', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{ border: 'none', background: '#F3F4F6', cursor: 'pointer', color: '#6B7280', padding: 7, borderRadius: 9, display: 'flex', alignItems: 'center', transition: 'background 0.15s, color 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#FFF1F2'; e.currentTarget.style.color = '#EF1D28'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#F3F4F6'; e.currentTarget.style.color = '#6B7280'; }}
          >
            <ArrowLeft size={18} />
          </button>
          <span style={{ fontWeight: 700, fontSize: 17, color: '#111827' }}>AI Video Interview</span>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 24px' }}>
          <div className="fade-up" style={{ width: '100%', maxWidth: 620 }}>
            <div style={{ textAlign: 'center', marginBottom: 30 }}>
              <div style={{
                width: 64, height: 64, borderRadius: 18, margin: '0 auto 18px',
                background: 'linear-gradient(135deg,#EF1D28,#FF6B6B)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 10px 28px rgba(239,29,40,0.30)',
              }}>
                <Mic size={28} color="white" strokeWidth={2} />
              </div>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: '#111827', margin: '0 0 10px', letterSpacing: '-0.02em' }}>Set Up Your Interview</h1>
              <p style={{ fontSize: 14.5, color: '#6B7280', margin: '0 0 18px', lineHeight: 1.5 }}>
                Tell us about the role and we'll tailor every question to it.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { icon: Globe, label: 'English only' },
                  { icon: Clock, label: '10–15 min' },
                  { icon: ListChecks, label: `Up to ${MAX_TOTAL_QUESTIONS} questions` },
                ].map(({ icon: Icon, label }) => (
                  <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: '#6B7280', background: '#F3F4F6', border: '1px solid #EAECF0', borderRadius: 20, padding: '5px 12px 5px 10px' }}>
                    <Icon size={12} strokeWidth={2.25} />
                    {label}
                  </span>
                ))}
              </div>
            </div>
            <div style={{ background: 'white', borderRadius: 22, padding: 32, boxShadow: '0 8px 32px rgba(15,23,42,0.07)', border: '1px solid #EAECF0' }}>
              <div style={{ marginBottom: 22 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 8 }}>
                  Job Title <span style={{ color: '#EF1D28' }}>*</span>
                </label>
                <input
                  value={jobTitle}
                  onChange={e => setJobTitle(e.target.value)}
                  placeholder="e.g. Senior Software Engineer"
                  style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #E5E7EB', borderRadius: 12, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'Inter,sans-serif', color: '#111827', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                  onFocus={e => { e.target.style.borderColor = '#EF1D28'; e.target.style.boxShadow = '0 0 0 4px rgba(239,29,40,0.10)'; }}
                  onBlur={e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 8 }}>
                  Job Description <span style={{ color: '#EF1D28' }}>*</span>
                </label>
                <textarea
                  value={jobDescription}
                  onChange={e => setJobDescription(e.target.value)}
                  placeholder="Paste the job description to get personalized follow-up questions…"
                  rows={6}
                  style={{ width: '100%', padding: '12px 14px', border: '1.5px solid #E5E7EB', borderRadius: 12, fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'Inter,sans-serif', color: '#111827', lineHeight: 1.5, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                  onFocus={e => { e.target.style.borderColor = '#EF1D28'; e.target.style.boxShadow = '0 0 0 4px rgba(239,29,40,0.10)'; }}
                  onBlur={e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
              <div style={{ background: '#FAFAFB', borderRadius: 14, padding: 18, marginBottom: 26, border: '1px solid #EAECF0' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>What to expect</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    'AI greets you by name and asks if you\'re ready',
                    'First question: Tell me about yourself + experience',
                    'Each follow-up question is based on your previous answer',
                    'Speech-to-text captures your spoken responses in real time',
                    'Detailed score report after the interview',
                  ].map((tip, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                        <CheckCircle2 size={12} color="#059669" strokeWidth={2.5} />
                      </div>
                      <span style={{ fontSize: 12.5, color: '#6B7280', lineHeight: 1.45 }}>{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={handleSetupNext}
                disabled={!canProceed}
                style={{
                  width: '100%', padding: '14px 0',
                  background: canProceed ? 'linear-gradient(135deg,#EF1D28,#C5161D)' : '#E5E7EB',
                  color: canProceed ? 'white' : '#9CA3AF',
                  border: 'none', borderRadius: 12, fontSize: 14.5, fontWeight: 700,
                  cursor: canProceed ? 'pointer' : 'not-allowed',
                  fontFamily: 'Inter,sans-serif',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  boxShadow: canProceed ? '0 6px 18px rgba(239,29,40,0.30)' : 'none',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={e => { if (canProceed) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 26px rgba(239,29,40,0.38)'; } }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = canProceed ? '0 6px 18px rgba(239,29,40,0.30)' : 'none'; }}
              >
                Next: Set Up Camera & Mic
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
        {dailyLimitError && <DailyLimitModal onClose={clearDailyLimitError} />}
      </div>
    );
  }

  // ── READY ─────────────────────────────────────────────────
  if (step === 'ready') {
    return (
      <div style={{ minHeight: '100vh', background: '#0D1117', display: 'flex', flexDirection: 'column', fontFamily: 'Inter,sans-serif' }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ background: '#1F2937', borderBottom: '1px solid #374151', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setStep('setup')} style={{ border: 'none', background: '#374151', cursor: 'pointer', color: '#9CA3AF', padding: 7, borderRadius: 9, display: 'flex', alignItems: 'center' }}>
            <ArrowLeft size={18} />
          </button>
          <span style={{ fontWeight: 700, fontSize: 17, color: 'white' }}>Camera & Microphone Setup</span>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
          <div style={{ width: '100%', maxWidth: 860, display: 'flex', borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            {/* Camera */}
            <div style={{ flex: 1, background: '#0D1117', position: 'relative', minHeight: 440 }}>
              <video ref={videoRef} autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              {!cameraGranted && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                  <VideoOff size={48} color="#374151" />
                  <span style={{ fontSize: 14, color: '#6B7280' }}>Camera not enabled</span>
                </div>
              )}
              <div style={{ position: 'absolute', bottom: 16, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 10 }}>
                {[{ ok: cameraGranted, On: Video, Off: VideoOff, label: 'Camera' }, { ok: micGranted, On: Mic, Off: MicOff, label: 'Mic' }].map(({ ok, On, Off, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, background: ok ? 'rgba(6,95,70,0.7)' : 'rgba(127,29,29,0.7)', border: `1px solid ${ok ? '#10B98144' : '#EF444444'}`, borderRadius: 20, padding: '6px 14px', backdropFilter: 'blur(4px)' }}>
                    {ok ? <On size={13} color="#10B981" /> : <Off size={13} color="#EF4444" />}
                    <span style={{ fontSize: 12, fontWeight: 600, color: ok ? '#10B981' : '#EF4444' }}>{label} {ok ? 'On' : 'Off'}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Panel */}
            <div style={{ width: 300, background: 'white', padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>💻</div>
                <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>We will email your detailed interview results after you finish.</div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>Your Name</label>
                <input value={userName} onChange={e => setUserName(e.target.value)} style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'Inter,sans-serif', color: '#111827' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5 }}>Email</label>
                <input value={userEmail} onChange={e => setUserEmail(e.target.value)} type="email" style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'Inter,sans-serif', color: '#111827' }} />
              </div>
              {!cameraGranted && (
                <button onClick={requestCamera} style={{ width: '100%', padding: 10, background: '#1F2937', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
                  Grant Camera & Mic Access
                </button>
              )}
              {!SR && (
                <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#92400E', lineHeight: 1.4 }}>
                  For speech recognition, use Chrome or Edge.
                </div>
              )}
              <button onClick={handleStartInterview} disabled={!cameraGranted || !micGranted} style={{ width: '100%', marginTop: 'auto', padding: 13, background: cameraGranted && micGranted ? 'linear-gradient(135deg,#EF1D28,#C5161D)' : '#E5E7EB', color: cameraGranted && micGranted ? 'white' : '#9CA3AF', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: cameraGranted && micGranted ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: 'Inter,sans-serif', boxShadow: cameraGranted && micGranted ? '0 4px 14px rgba(239,29,40,0.30)' : 'none' }}>
                Start Interview →
              </button>
            </div>
          </div>
        </div>
        {dailyLimitError && <DailyLimitModal onClose={clearDailyLimitError} />}
      </div>
    );
  }

  // ── INTERVIEW ─────────────────────────────────────────────
  if (step === 'interview') {
    const isAiTurn  = phase === 'intro' || phase === 'ai-speaking' || phase === 'generating-question';
    const isWaiting = phase === 'waiting-ready';
    const isUserTurn = phase === 'user-answering';
    const isGenerating = phase === 'generating-question';

    const phaseLabel = {
      intro: 'Greeting…',
      'waiting-ready': 'Waiting for response…',
      'generating-question': 'Thinking…',
      'ai-speaking': 'Speaking…',
      'user-answering': 'Listening',
    }[phase] || '';

    return (
      <div style={{ height: '100vh', background: '#0D1117', display: 'flex', flexDirection: 'column', color: 'white', fontFamily: 'Inter,sans-serif', overflow: 'hidden' }}>
        <style>{`
          @keyframes spin  { to { transform: rotate(360deg); } }
          @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
          @keyframes glow  { 0%,100%{box-shadow:0 0 8px #22D3EE44} 50%{box-shadow:0 0 22px #22D3EE99} }
        `}</style>

        {/* Top bar */}
        <div style={{ background: '#111827', borderBottom: '1px solid #1F2937', padding: '0 20px', height: 54, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#EF4444', animation: 'pulse 1.5s ease-in-out infinite' }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#9CA3AF' }}>Recording</span>
          </div>
          <span style={{ fontSize: 18, fontWeight: 800, color: 'white', letterSpacing: 2, fontVariantNumeric: 'tabular-nums' }}>{fmtTime(elapsed)}</span>
          <span style={{ fontSize: 12, color: '#6B7280' }}>
            {isWaiting ? 'Say "Ready" to begin' : (questionDisplay || (currentQIdx > 0 ? `Q ${currentQIdx}` : ''))}
          </span>
        </div>

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Main */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px 20px', gap: 12, overflow: 'hidden' }}>
            {/* Video feeds */}
            <div style={{ display: 'flex', gap: 14, flex: 1, minHeight: 0, maxHeight: 310 }}>
              {/* User */}
              <div style={{ flex: 1, background: '#1F2937', borderRadius: 14, overflow: 'hidden', position: 'relative', border: isUserTurn ? '2px solid #22D3EE' : '2px solid #374151', transition: 'border-color 0.3s', animation: isUserTurn ? 'glow 2s ease-in-out infinite' : 'none' }}>
                <video ref={videoRef} autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', bottom: 10, left: 12, fontSize: 12, fontWeight: 600, color: 'white', background: 'rgba(0,0,0,0.65)', padding: '3px 10px', borderRadius: 6 }}>
                  {userName || 'You'}
                </div>
                {isUserTurn && (
                  <div style={{ position: 'absolute', top: 10, right: 12, display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(0,0,0,0.65)', borderRadius: 6, padding: '3px 9px' }}>
                    <Mic size={11} color="#22D3EE" />
                    <span style={{ fontSize: 11, color: '#22D3EE', fontWeight: 600 }}>Listening</span>
                  </div>
                )}
                {isWaiting && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: 'rgba(0,0,0,0.7)', borderRadius: 12, padding: '10px 18px', fontSize: 13, color: '#22D3EE', fontWeight: 600 }}>Say "Yes, I'm ready" →</div>
                  </div>
                )}
              </div>

              {/* AI avatar */}
              <div style={{ flex: 1, background: '#1F2937', borderRadius: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', border: isAiTurn ? '2px solid #22D3EE' : '2px solid #374151', transition: 'border-color 0.3s', animation: isAiTurn ? 'glow 1.5s ease-in-out infinite' : 'none' }}>
                {isGenerating
                  ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                      <Loader2 size={42} color="#22D3EE" style={{ animation: 'spin 1s linear infinite' }} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#22D3EE', letterSpacing: 0.5 }}>
                        {analysisInProgress ? 'Analyzing answer…' : 'Thinking…'}
                      </span>
                    </div>
                  : <div style={{ fontSize: 60, filter: isAiTurn ? 'drop-shadow(0 0 18px #22D3EE)' : 'none', transition: 'filter 0.3s', marginBottom: 8 }}>🤖</div>
                }
                <span style={{ fontSize: 12, fontWeight: 600, color: '#6B7280' }}>MarsResume AI</span>
                {isAiTurn && (
                  <div style={{ position: 'absolute', top: 10, right: 12, display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(0,0,0,0.65)', borderRadius: 6, padding: '3px 9px' }}>
                    <span style={{ fontSize: 11, color: '#22D3EE', fontWeight: 600 }}>{phaseLabel}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Question / intro text bubble */}
            <div style={{ background: '#1F2937', borderRadius: 12, padding: '12px 18px', border: '1px solid #374151', flexShrink: 0, minHeight: 56 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#22D3EE', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 5 }}>
                {phase === 'intro' || phase === 'waiting-ready' ? 'MarsResume AI' : `Question ${currentQIdx}`}
              </div>
              <div style={{ fontSize: 14, color: currentQText ? 'white' : '#374151', lineHeight: 1.6, fontStyle: currentQText ? 'normal' : 'italic' }}>
                {currentQText || (isGenerating ? (analysisInProgress ? 'Evaluating your answer…' : 'Generating next question…') : 'Preparing…')}
              </div>
            </div>

            {/* Live transcript */}
            <div style={{ background: '#161B22', borderRadius: 10, padding: '10px 14px', flexShrink: 0, border: '1px solid #1F2937', minHeight: 48 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#4B5563', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Your answer (live)</div>
              <div style={{ fontSize: 13, color: '#9CA3AF', lineHeight: 1.5 }}>
                {sessionTranscript && <span>{sessionTranscript}</span>}
                {interimText && <span style={{ color: '#6B7280', fontStyle: 'italic' }}>{interimText}</span>}
                {!sessionTranscript && !interimText && (
                  <span style={{ color: '#374151', fontStyle: 'italic' }}>
                    {isUserTurn ? 'Start speaking…' : isWaiting ? 'Say "Yes, I\'m ready"…' : 'Waiting for AI…'}
                  </span>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexShrink: 0 }}>
              {/* Next Question — only visible when the user has started speaking */}
              {phase === 'user-answering' && sessionTranscript.trim().length > 5 && (
                <button
                  onClick={() => advanceFnRef.current(currentQRef.current || { question: '', hint: '' })}
                  style={{ padding: '9px 24px', background: '#065F46', border: '1px solid #10B981', borderRadius: 8, color: '#A7F3D0', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#047857'}
                  onMouseLeave={e => e.currentTarget.style.background = '#065F46'}
                >
                  Next Question →
                </button>
              )}
              <button onClick={handleStopInterview} style={{ padding: '9px 28px', background: '#7F1D1D', border: '1px solid #EF4444', borderRadius: 8, color: '#FCA5A5', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }} onMouseEnter={e => e.currentTarget.style.background = '#991B1B'} onMouseLeave={e => e.currentTarget.style.background = '#7F1D1D'}>
                Stop Interview
              </button>
            </div>
          </div>

          {/* Transcript sidebar */}
          <div style={{ width: 268, background: '#111827', borderLeft: '1px solid #1F2937', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #1F2937', flexShrink: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7 }}>Transcript</span>
            </div>
            <div ref={transcriptSidebarRef} style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {transcripts.map((t, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: t.role === 'ai' ? '#22D3EE' : '#A78BFA', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {t.role === 'ai' ? '🤖 MarsResume AI' : '👤 You'}
                  </span>
                  <div style={{ fontSize: 12, color: '#D1D5DB', lineHeight: 1.5, background: t.role === 'ai' ? '#1F2937' : '#1C1530', borderRadius: 8, padding: '7px 10px', border: `1px solid ${t.role === 'ai' ? '#374151' : '#2D1F4E'}` }}>
                    {t.text.length > 200 ? t.text.slice(0, 200) + '…' : t.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Silence popup */}
        {showSilencePopup && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}>
            <div style={{ background: '#1F2937', border: '1px solid #374151', borderRadius: 20, padding: '36px 44px', textAlign: 'center', maxWidth: 360, width: '90%' }}>
              <div style={{ fontSize: 44, marginBottom: 14 }}>😶</div>
              <h3 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'white' }}>Still there?</h3>
              <p style={{ margin: '0 0 22px', fontSize: 14, color: '#9CA3AF' }}>We haven't detected any speech in a while.</p>
              <div style={{ width: 68, height: 68, borderRadius: '50%', background: '#111827', border: '3px solid #EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 800, color: '#EF4444', margin: '0 auto 26px' }}>
                {silenceCountdown}
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={handleResumeFromPopup} style={{ padding: '10px 18px', background: '#111827', border: '1px solid #374151', borderRadius: 8, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
                  Resume Speaking
                </button>
                <button onClick={() => { setShowSilencePopup(false); advanceFnRef.current(currentQRef.current || { question: '', hint: '' }); }} style={{ padding: '10px 18px', background: '#065F46', border: '1px solid #10B981', borderRadius: 8, color: '#A7F3D0', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
                  Next Question →
                </button>
                <button onClick={() => { setShowSilencePopup(false); stoppedRef.current = true; advanceFnRef.current(currentQRef.current || { question: '', hint: '' }, true); }} style={{ padding: '10px 18px', background: '#7F1D1D', border: '1px solid #EF4444', borderRadius: 8, color: '#FCA5A5', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
                  End Interview
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── ANALYZING ─────────────────────────────────────────────
  if (step === 'analyzing') {
    return (
      <div style={{ minHeight: '100vh', background: '#0D1117', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: 'Inter,sans-serif', gap: 16 }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <Loader2 size={52} color="#22D3EE" style={{ animation: 'spin 1s linear infinite' }} />
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Analyzing your interview…</h2>
        <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>Evaluating your responses. This may take a moment.</p>
      </div>
    );
  }

  // ── REPORT ────────────────────────────────────────────────
  if (step === 'report') {
    const totalScore    = evaluations.reduce((s, e) => s + (e.score || 0), 0);
    const maxRaw        = qaHistory.length * 10;
    const weightedTotal = evaluations.reduce((s, e) => s + (e.score || 0) * weightageInfo(e.weightage).factor, 0);
    const weightedMax   = evaluations.reduce((s, e) => s + 10 * weightageInfo(e.weightage).factor, 0);
    const weightedPct   = weightedMax > 0 ? Math.round((weightedTotal / weightedMax) * 100) : 0;
    const overall       = scoreInfo(maxRaw > 0 ? Math.round((totalScore / maxRaw) * 10) : 0);
    const answeredCount = qaHistory.filter(q => q.answer && q.answer !== '(No answer provided)').length;

    // No answers recorded — show a friendly "try again" state instead of a blank/fake report
    if (answeredCount === 0) {
      return (
        <div style={{ minHeight: '100vh', background: '#F8F9FB', fontFamily: 'Inter,sans-serif', display: 'flex', flexDirection: 'column' }}>
          <div style={{ background: 'white', borderBottom: '1px solid #EAECF0', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => navigate('/dashboard')} style={{ border: 'none', background: '#F3F4F6', cursor: 'pointer', color: '#6B7280', padding: 7, borderRadius: 9, display: 'flex', alignItems: 'center' }}>
              <ArrowLeft size={18} />
            </button>
            <span style={{ fontWeight: 700, fontSize: 17, color: '#111827' }}>Interview Report</span>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 52 }}>🎙️</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>No Answers Recorded</h2>
            <p style={{ fontSize: 14, color: '#6B7280', margin: 0, maxWidth: 380, lineHeight: 1.6 }}>
              The interview ended before any questions were answered. Speak your responses after the AI asks each question, then pause — the next question will follow automatically.
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button onClick={handleTryAgain} style={{ border: 'none', background: 'linear-gradient(135deg,#EF1D28,#C5161D)', color: 'white', borderRadius: 10, padding: '12px 26px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif', boxShadow: '0 4px 14px rgba(239,29,40,0.25)' }}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    const likelyColors = {
      strong:           { color: '#059669', bg: '#ECFDF5', label: 'Strong Hire' },
      moderate:         { color: '#D97706', bg: '#FFFBEB', label: 'Moderate Fit' },
      weak:             { color: '#EA580C', bg: '#FFF7ED', label: 'Needs Work' },
      'not recommended':{ color: '#DC2626', bg: '#FEF2F2', label: 'Not Recommended' },
    };
    const likely = likelyColors[finalReport?.hiringLikelihood] || likelyColors.moderate;

    const ScoreBar = ({ label, value, color }) => (
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{label}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color }}>{value}%</span>
        </div>
        <div style={{ height: 6, background: '#F3F4F6', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${value}%`, background: color, borderRadius: 4, transition: 'width 0.8s ease' }} />
        </div>
      </div>
    );

    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FB', fontFamily: 'Inter,sans-serif' }}>
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @media print {
            .no-print { display: none !important; }
            body { background: white !important; }
            .report-card { box-shadow: none !important; break-inside: avoid; }
          }
        `}</style>

        {/* Sticky header */}
        <div className="no-print" style={{ background: 'white', borderBottom: '1px solid #EAECF0', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => navigate('/dashboard')} style={{ border: 'none', background: '#F3F4F6', cursor: 'pointer', color: '#6B7280', padding: 7, borderRadius: 9, display: 'flex', alignItems: 'center' }}>
              <ArrowLeft size={18} />
            </button>
            <span style={{ fontWeight: 700, fontSize: 17, color: '#111827' }}>Interview Report</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => window.print()} style={{ border: '1.5px solid #22D3EE', background: 'white', cursor: 'pointer', color: '#0891B2', padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: 'Inter,sans-serif' }}>
              Save as PDF
            </button>
            <button onClick={handleTryAgain} style={{ border: '1.5px solid #E5E7EB', background: 'white', cursor: 'pointer', color: '#374151', padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: 'Inter,sans-serif' }}>
              New Interview
            </button>
          </div>
        </div>

        <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 20px 60px' }}>

          {/* ── Section 1: Overall Summary ─────────────────────── */}
          <div className="report-card" style={{ background: 'white', borderRadius: 16, padding: '24px 28px', marginBottom: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid #EAECF0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
              {/* Score ring */}
              <div style={{ width: 90, height: 90, borderRadius: '50%', flexShrink: 0, background: finalReport ? '#F5F3FF' : overall.bg, border: `4px solid ${finalReport ? '#7C3AED' : overall.color}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 26, fontWeight: 800, color: finalReport ? '#7C3AED' : overall.color, lineHeight: 1 }}>
                  {finalReport ? finalReport.overallScore : totalScore}
                </span>
                <span style={{ fontSize: 11, fontWeight: 600, color: finalReport ? '#7C3AED' : overall.color, opacity: 0.75 }}>
                  {finalReport ? '/100' : `/${maxRaw}`}
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 19, fontWeight: 800, color: '#111827', marginBottom: 3 }}>{jobTitle}</div>
                <div style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 10 }}>
                  {qaHistory.length} questions · {fmtTime(elapsed)} · AI Video Interview · {answeredCount}/{qaHistory.length} answered
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {finalReport ? (
                    <span style={{ fontSize: 12, fontWeight: 700, color: likely.color, background: likely.bg, borderRadius: 6, padding: '3px 12px', border: `1px solid ${likely.color}33` }}>
                      {likely.label}
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, fontWeight: 700, color: overall.color, background: overall.bg, borderRadius: 6, padding: '3px 12px', border: `1px solid ${overall.color}33` }}>
                      Overall: {overall.label}
                    </span>
                  )}
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#0891B2', background: '#ECFEFF', borderRadius: 6, padding: '3px 10px', border: '1px solid #A5F3FC' }}>
                    Score: {totalScore}/{maxRaw}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#7C3AED', background: '#F5F3FF', borderRadius: 6, padding: '3px 10px', border: '1px solid #DDD6FE' }}>
                    Weighted: {weightedPct}%
                  </span>
                </div>
                {finalReport?.summary && (
                  <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, marginTop: 12, marginBottom: 0, fontStyle: 'italic' }}>
                    "{finalReport.summary}"
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ── Section 2: Performance Breakdown + Insights (2-col) ── */}
          {finalReport && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              {/* Breakdown bars */}
              <div className="report-card" style={{ background: 'white', borderRadius: 14, padding: '20px 22px', border: '1px solid #EAECF0', boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>Performance Breakdown</div>
                <ScoreBar label="Technical"     value={finalReport.technicalScore}     color="#3B82F6" />
                <ScoreBar label="Communication" value={finalReport.communicationScore} color="#8B5CF6" />
                <ScoreBar label="Confidence"    value={finalReport.confidenceScore}    color="#10B981" />
                <ScoreBar label="Behavioral"    value={finalReport.behavioralScore}    color="#F59E0B" />
              </div>
              {/* Best / Weakest highlight */}
              <div className="report-card" style={{ background: 'white', borderRadius: 14, padding: '20px 22px', border: '1px solid #EAECF0', boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>Notable Moments</div>
                {finalReport.strongestAnswer && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#059669', marginBottom: 5 }}>BEST ANSWER</div>
                    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{finalReport.strongestAnswer}</div>
                  </div>
                )}
                {finalReport.weakestAnswer && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#DC2626', marginBottom: 5 }}>NEEDS WORK</div>
                    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{finalReport.weakestAnswer}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Section 3: Strengths + Improvement Areas (2-col) ─── */}
          {finalReport && (finalReport.strengths.length > 0 || finalReport.improvementAreas.length > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div className="report-card" style={{ background: '#F0FDF4', borderRadius: 14, padding: '18px 20px', border: '1px solid #BBF7D0' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#15803D', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Key Strengths</div>
                {finalReport.strengths.map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                    <span style={{ color: '#15803D', fontWeight: 700, flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: 13, color: '#166534', lineHeight: 1.4 }}>{s}</span>
                  </div>
                ))}
              </div>
              <div className="report-card" style={{ background: '#FFFBEB', borderRadius: 14, padding: '18px 20px', border: '1px solid #FDE68A' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#B45309', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Areas to Improve</div>
                {finalReport.improvementAreas.map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                    <span style={{ color: '#B45309', fontWeight: 700, flexShrink: 0 }}>→</span>
                    <span style={{ fontSize: 13, color: '#92400E', lineHeight: 1.4 }}>{a}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Section 4: Speaking Metrics ────────────────────── */}
          {communicationMetrics.answers > 0 && (
            <div className="report-card" style={{ background: 'white', borderRadius: 12, padding: '16px 20px', marginBottom: 14, border: '1px solid #EAECF0', display: 'flex', gap: 0, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, width: '100%', marginBottom: 12 }}>Speaking Metrics</div>
              {[
                { label: 'Total Words', value: communicationMetrics.totalWords.toLocaleString(), color: '#3B82F6' },
                { label: 'Avg / Answer', value: Math.round(communicationMetrics.totalWords / (communicationMetrics.answers || 1)), color: '#8B5CF6' },
                { label: 'Filler Words', value: communicationMetrics.fillerWords, color: communicationMetrics.fillerWords > 15 ? '#EF4444' : '#10B981' },
                { label: 'Filler Rate', value: `${communicationMetrics.totalWords > 0 ? Math.round(communicationMetrics.fillerWords / communicationMetrics.totalWords * 100) : 0}%`, color: '#F59E0B' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ flex: '1 1 120px', textAlign: 'center', padding: '4px 12px', borderRight: '1px solid #F3F4F6' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>{label}</div>
                </div>
              ))}
            </div>
          )}

          {/* ── Section 5: Recommended Next Steps ──────────────── */}
          {finalReport?.recommendedNextSteps?.length > 0 && (
            <div className="report-card" style={{ background: '#EFF6FF', borderRadius: 12, padding: '16px 20px', marginBottom: 20, border: '1px solid #BFDBFE' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#1E40AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Recommended Next Steps</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {finalReport.recommendedNextSteps.map((step, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: '1 1 200px' }}>
                    <span style={{ minWidth: 22, height: 22, borderRadius: '50%', background: '#2563EB', color: 'white', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
                    <span style={{ fontSize: 13, color: '#1E40AF', lineHeight: 1.45 }}>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Section 6: Weightage legend ────────────────────── */}
          <div className="report-card" style={{ background: 'white', borderRadius: 10, padding: '10px 16px', marginBottom: 16, border: '1px solid #EAECF0', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Question Priority:</span>
            {[['high','#EF4444','Critical skill'], ['medium','#F59E0B','Standard Q'], ['low','#6B7280','General']].map(([w, c, desc]) => (
              <span key={w} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: c, display: 'inline-block' }} />
                <span style={{ fontWeight: 700, color: c }}>{w.charAt(0).toUpperCase() + w.slice(1)}</span>
                <span style={{ color: '#9CA3AF' }}>— {desc}</span>
              </span>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9CA3AF' }}>
              {qaHistory.filter(q => q.type !== 'main').length > 0 && `${qaHistory.filter(q => q.type !== 'main').length} follow-up${qaHistory.filter(q => q.type !== 'main').length !== 1 ? 's' : ''} asked`}
            </span>
          </div>

          {(() => {
            const mail = {
              idle:    { bg: '#F8F9FB', border: '#EAECF0', color: '#6B7280' },
              sending: { bg: '#EFF6FF', border: '#BFDBFE', color: '#1E40AF' },
              sent:    { bg: '#F0FDF4', border: '#BBF7D0', color: '#15803D' },
              failed:  { bg: '#FFF7ED', border: '#FED7AA', color: '#9A3412' },
            }[emailStatus];
            const recipient = userEmail || user?.email;
            return (
              <div className="no-print" style={{ background: mail.bg, border: `1px solid ${mail.border}`, borderRadius: 10, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: mail.color, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {emailStatus === 'sending' && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />}
                <span>
                  {emailStatus === 'sending' && <>Emailing your PDF report to <strong>{recipient}</strong>…</>}
                  {emailStatus === 'sent'    && <>📧 Your PDF report has been emailed to <strong>{recipient}</strong>.</>}
                  {emailStatus === 'failed'  && <>We couldn't email your report{emailError ? ` — ${emailError}` : '.'} You can still save it as a PDF below.</>}
                  {emailStatus === 'idle'    && <>Save this report as a PDF below to keep a copy.</>}
                </span>
                {emailStatus === 'failed' && savedInterviewId && (
                  <button
                    onClick={() => sendReportEmail(savedInterviewId, recipient)}
                    style={{ marginLeft: 'auto', border: '1px solid #FDBA74', background: 'white', color: '#9A3412', borderRadius: 8, padding: '5px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}
                  >
                    Try again
                  </button>
                )}
              </div>
            );
          })()}

          {/* ── Section 7: Per-question cards ──────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {qaHistory.map((qa, i) => {
              const ev       = evaluations[i] || {};
              const si       = scoreInfo(ev.score ?? (qa.analysis?.score ?? 0));
              const wi       = weightageInfo(ev.weightage);
              const noAnswer = qa.answer === '(No answer provided)';
              const qLabel   = qa.type === 'clarify' ? 'Clarification' : qa.type === 'followup' ? 'Follow-up' : `Main Question`;
              const qLabelColor = qa.type === 'main' ? '#6B7280' : '#7C3AED';
              return (
                <div key={i} className="report-card" style={{ background: 'white', borderRadius: 14, padding: '20px 22px', boxShadow: '0 1px 10px rgba(0,0,0,0.05)', border: '1px solid #EAECF0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>Q{i + 1}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: qLabelColor, background: qa.type === 'main' ? '#F3F4F6' : '#F5F3FF', borderRadius: 4, padding: '1px 7px' }}>{qLabel}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: wi.color, background: wi.bg, borderRadius: 5, padding: '2px 10px', border: `1px solid ${wi.color}33` }}>{wi.label}</span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: si.color, background: si.bg, borderRadius: 5, padding: '2px 11px', border: `1px solid ${si.color}33` }}>
                        {ev.score ?? qa.analysis?.score ?? '–'}/10 · {si.label}
                      </span>
                    </div>
                  </div>

                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', lineHeight: 1.55, marginBottom: 12 }}>{qa.question}</div>

                  <div style={{ background: '#F8F9FB', borderRadius: 8, padding: '10px 14px', marginBottom: 14, border: '1px solid #EAECF0' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>Your Answer</div>
                    <div style={{ fontSize: 13, color: noAnswer ? '#9CA3AF' : '#374151', lineHeight: 1.6, fontStyle: noAnswer ? 'italic' : 'normal' }}>{qa.answer}</div>
                  </div>

                  {ev.strengths?.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Strengths</div>
                      {ev.strengths.map((s, j) => (
                        <div key={j} style={{ display: 'flex', gap: 8, marginBottom: 4, alignItems: 'flex-start' }}>
                          <span style={{ color: '#059669', flexShrink: 0 }}>+</span>
                          <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>{s}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {ev.improvements?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#D97706', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>To Improve</div>
                      {ev.improvements.map((imp, j) => (
                        <div key={j} style={{ display: 'flex', gap: 8, marginBottom: 4, alignItems: 'flex-start' }}>
                          <span style={{ color: '#D97706', flexShrink: 0 }}>→</span>
                          <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>{imp}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="no-print" style={{ marginTop: 28, display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={() => window.print()} style={{ border: 'none', background: 'linear-gradient(135deg,#0891B2,#0E7490)', cursor: 'pointer', color: 'white', padding: '12px 32px', borderRadius: 10, fontSize: 14, fontWeight: 700, fontFamily: 'Inter,sans-serif' }}>
              Save Report as PDF
            </button>
            <button onClick={handleTryAgain} style={{ border: '1.5px solid #E5E7EB', background: 'white', cursor: 'pointer', color: '#374151', padding: '12px 32px', borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: 'Inter,sans-serif' }}>
              New Interview
            </button>
          </div>
        </div>
        {dailyLimitError && <DailyLimitModal onClose={clearDailyLimitError} />}
      </div>
    );
  }

  return null;
};
